import { BlockedIcon } from "@/components/icons/BlockedIcon";
import { IconSize } from "@/components/icons/IconSize";
import { formatCompanyName } from "@/lib/formatters/CompanyNameFormatter";
import { testIdConstants } from "@/types/test-id-constants/testIdConstants";
import { Box, Row, Space, Text } from "@stenajs-webui/core";
import { Icon, stenaLockClosed, stenaSearch } from "@stenajs-webui/elements";
import { Select } from "@stenajs-webui/select";
import { cssColor } from "@stenajs-webui/theme";
import {
  GroupBase,
  SelectComponentsConfig,
  SingleValue,
  StylesConfig,
  components,
} from "react-select";
import {
  CustomerStatusCode,
  InfoSearchBySComerNumberOrIndexFragment,
} from "@/gql/graphql";
import { useEffect, useMemo, useState } from "react";
import { UseSearchCustomers } from "./UseSearchCustomer";

const selectComponents: SelectComponentsConfig<
  CustomerOption,
  false,
  GroupBase<CustomerOption>
> = {
  IndicatorSeparator: () => null,
  DropdownIndicator: () => (
    <Icon icon={stenaSearch} style={{ paddingRight: 10 }} />
  ),
  Option: ({ children, ...props }) => {
    const { data: customer } = props.data as CustomerOption;
    return (
      <components.Option {...props}>
        <Box gap={1}>
          <Row alignItems={"center"}>
            <Text variant={"bold"}>{formatCompanyName(customer.name)}</Text>
            {customer.custStatus?.code === CustomerStatusCode.Blocked && (
              <>
                <Space num={2} />
                <BlockedIcon size={IconSize.Small} />
                <Space num={1} />
                <Text size="small" color={cssColor("--lhds-color-ui-500")}>
                  {customer.custStatus?.name}
                </Text>
              </>
            )}
            {customer.custStatus?.code === CustomerStatusCode.Closed && (
              <>
                <Space num={2} />
                <Icon icon={stenaLockClosed} />
                <Space num={1} />
                <Text size="small" color={cssColor("--lhds-color-ui-500")}>
                  {customer.custStatus?.name}
                </Text>
              </>
            )}
          </Row>
          <Row>
            <Box width={140}>
              <Text size="small" color={cssColor("--lhds-color-ui-500")}>
                {customer.custIndex} ({customer.custNo})
              </Text>
            </Box>
            {customer.parent && (
              <Text size="small" color={cssColor("--lhds-color-ui-500")}>
                Child Account: Parent No. {customer.parent.id}
              </Text>
            )}
          </Row>
        </Box>
      </components.Option>
    );
  },
};

const MIN_SEARCH_LENGTH = 3;

interface CustomerOption {
  label: string;
  value: string;
  data: InfoSearchBySComerNumberOrIndexFragment;
}

export const ApplicationSearch = ({
  onChange,
  value,
  styles,
}: {
  value: SingleValue<CustomerOption> | null;
  onChange: (event: SingleValue<CustomerOption>) => void;
  styles:
    | StylesConfig<CustomerOption, false, GroupBase<CustomerOption>>
    | undefined;
}) => {
  const { searchedCustomers, searchCustomers, loading } = UseSearchCustomers();
  const [searchValue, setSearchValue] = useState("");

  const isSearchable = searchValue.length >= MIN_SEARCH_LENGTH;

  /**
   * Derived rather than stored: repeating a search returns the same cached
   * result object, so an effect keyed on it would never refill a cleared list.
   */
  const options = useMemo<CustomerOption[]>(
    () =>
      isSearchable && searchedCustomers
        ? searchedCustomers.map((customer) => ({
            value: customer.id,
            label: customer.custIndex,
            data: customer,
          }))
        : [],
    [isSearchable, searchedCustomers]
  );

  useEffect(() => {
    if (isSearchable) {
      searchCustomers(searchValue);
    } else {
      searchCustomers.cancel();
    }
  }, [isSearchable, searchCustomers, searchValue]);

  useEffect(() => () => searchCustomers.cancel(), [searchCustomers]);
  return (
    <Box data-testid={testIdConstants.globalCustomerSearch} minWidth={485}>
      <Select
        styles={styles}
        components={selectComponents}
        inputValue={searchValue}
        onInputChange={(value) => {
          setSearchValue(value);
        }}
        options={options}
        isLoading={isSearchable && loading}
        value={value}
        onChange={onChange}
      />
    </Box>
  );
};
